import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, CircularProgress, Chip, Button, ToggleButtonGroup, ToggleButton, Badge, Popover, List, ListItem, ListItemText, ListItemSecondaryAction, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';
import { setSpecialOfferDialog } from './selfOrderEssentialsSlice';
import { setSelfCartArray, setSelfCartModified } from '../../Slices/selfOrderCartSlice';
import { formatCurrencyValue } from '../../SelfGeneralFunctions';

const SelfSpecialOffersDialog = ({ addToCart }) => {
  const dispatch = useDispatch();
  const { specialOfferDialog } = useSelector((state) => state.selfOrderEssentialsSlice);
  const { specialItemsListForBilling } = useSelector((state) => state.specialItemsSlice);
  const { submitLoading } = useSelector((state) => state.mainDashBoardSlice);
  const { currencyDetails } = useSelector((state) => state.mainDashBoardSlice);
  const { cartArray } = useSelector((state) => state.selfOrderCartSlice);
  const decimalPosition = localStorage.getItem('decimalPosition');
  
  const [filter, setFilter] = useState('all'); // 'all', 'combo', 'special'
  const [cartAnchorEl, setCartAnchorEl] = useState(null);

  const handleClose = () => {
    dispatch(setSpecialOfferDialog(false));
    setFilter('all');
    setCartAnchorEl(null);
  };
  
  const handleCartClick = (event) => {
    setCartAnchorEl(event.currentTarget);
  };
  
  const handleCartPopoverClose = () => {
    setCartAnchorEl(null);
  };
  
  const handleDeleteCartItem = (itemId) => {
    const updatedCart = cartArray.filter(item => item._id !== itemId);
    dispatch(setSelfCartArray(updatedCart));
    dispatch(setSelfCartModified(true));
  };
  
  const cartPopoverOpen = Boolean(cartAnchorEl);
  
  const filteredOffers = useMemo(() => {
    if (!specialItemsListForBilling) return [];
    if (filter === 'all') return specialItemsListForBilling;
    if (filter === 'combo') return specialItemsListForBilling.filter(o => o.isCombo === true);
    if (filter === 'special') return specialItemsListForBilling.filter(o => o.isCombo === false);
    return specialItemsListForBilling;
  }, [specialItemsListForBilling, filter]);
  
  const comboCount = specialItemsListForBilling?.filter(o => o.isCombo === true)?.length || 0;
  const specialCount = specialItemsListForBilling?.filter(o => o.isCombo === false)?.length || 0;
  
  // Calculate cart special/combo items count and total
  const cartSpecialItems = useMemo(() => {
    if (!cartArray) return { count: 0, total: 0, items: [] };
    const specialItems = cartArray.filter(item => item.isCombo === true);
    const count = specialItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    const total = specialItems.reduce((sum, item) => sum + ((item.salesPrice || 0) * (item.quantity || 1)), 0);
    return { count, total, items: specialItems };
  }, [cartArray]);

  const handleOfferClick = (offer) => {
    const comboProduct = {
      _id: offer._id,
      productName: offer.name || offer.productName,
      salesPrice: offer.salesPrice || offer.cost || 0,
      imageUrl: offer.imageUrl,
      isCombo: true,
      productList: offer.productList,
      taxes: offer.taxes,
      includedPrice: offer.includedPrice,
      uom: offer.uom
    };
    
    addToCart(comboProduct);
  };

  const getIncludedItems = (offer) => {
    if (!offer.productList || !Array.isArray(offer.productList)) return [];
    return offer.productList.slice(0, 4).map(item => item.productName || item.name).filter(Boolean);
  };

  const formatPrice = (price) => {
    return formatCurrencyValue(price, currencyDetails, decimalPosition);
  };

  return (
    <Dialog
      open={specialOfferDialog}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: 'linear-gradient(145deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
          color: '#fff',
          maxHeight: '85vh',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(90deg, #ff6b6b 0%, #ff8e53 100%)',
        py: 2,
        px: 3,
        gap: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalOfferIcon sx={{ fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#fff', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
              Special Offers
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Cart Summary for Special Items - Clickable */}
            {cartSpecialItems.count > 0 && (
              <Box 
                onClick={handleCartClick}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  bgcolor: 'rgba(0,0,0,0.25)',
                  borderRadius: '12px',
                  px: 2,
                  py: 0.8,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.4)',
                    transform: 'scale(1.02)'
                  }
                }}>
                <Badge badgeContent={cartSpecialItems.count} color="success" max={99}>
                  <ShoppingCartIcon sx={{ fontSize: 24, color: '#fff' }} />
                </Badge>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1, fontSize: '0.65rem' }}>
                    In Cart
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#fff', lineHeight: 1.2 }}>
                    {formatPrice(cartSpecialItems.total)}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Cart Items Popover */}
            <Popover
              open={cartPopoverOpen}
              anchorEl={cartAnchorEl}
              onClose={handleCartPopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '12px',
                  background: 'linear-gradient(145deg, #1e1e30 0%, #252540 100%)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  minWidth: 280,
                  maxWidth: 350,
                  maxHeight: 400,
                  overflow: 'hidden'
                }
              }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff' }}>
                  Special Items in Cart
                </Typography>
              </Box>
              <List sx={{ py: 0, maxHeight: 280, overflowY: 'auto' }}>
                {cartSpecialItems.items.map((item, index) => (
                  <React.Fragment key={item._id}>
                    <ListItem sx={{ py: 1.5, px: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                        {item.imageUrl ? (
                          <img 
                            src={item.imageUrl} 
                            alt={item.productName}
                            style={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: 8, 
                              objectFit: 'cover' 
                            }}
                          />
                        ) : (
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '8px', 
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <RestaurantMenuIcon sx={{ fontSize: 20, color: '#fff' }} />
                          </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#fff', 
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.productName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {item.quantity || 1} × {formatPrice(item.salesPrice)}
                          </Typography>
                        </Box>
                      </Box>
                      <ListItemSecondaryAction>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteCartItem(item._id)}
                          sx={{ 
                            color: '#ff6b6b',
                            '&:hover': { 
                              bgcolor: 'rgba(255,107,107,0.2)' 
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < cartSpecialItems.items.length - 1 && (
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
              <Box sx={{ 
                p: 2, 
                borderTop: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Total
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#00e676' }}>
                  {formatPrice(cartSpecialItems.total)}
                </Typography>
              </Box>
            </Popover>
            
            <IconButton onClick={handleClose} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Filter Toggle */}
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
          sx={{
            bgcolor: 'rgba(0,0,0,0.2)',
            borderRadius: '12px',
            '& .MuiToggleButton-root': {
              color: 'rgba(255,255,255,0.7)',
              border: 'none',
              px: 2,
              py: 0.8,
              fontSize: '0.85rem',
              fontWeight: 'bold',
              textTransform: 'none',
              '&.Mui-selected': {
                bgcolor: 'rgba(255,255,255,0.25)',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              },
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              }
            }
          }}
        >
          <ToggleButton value="all">
            <LocalOfferIcon sx={{ mr: 0.5, fontSize: 18 }} />
            All ({specialItemsListForBilling?.length || 0})
          </ToggleButton>
          <ToggleButton value="combo">
            <FastfoodIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Combos ({comboCount})
          </ToggleButton>
          <ToggleButton value="special">
            <WhatshotIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Special ({specialCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
        {submitLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress sx={{ color: '#ff6b6b' }} size={50} />
          </Box>
        ) : filteredOffers?.length > 0 ? (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2.5,
            py: 1
          }}>
            {filteredOffers.map((offer, index) => {
              const includedItems = getIncludedItems(offer);
              const price = offer.salesPrice || offer.cost || 0;
              
              return (
                <Box
                  key={offer._id || index}
                  sx={{
                    borderRadius: '16px',
                    background: 'linear-gradient(145deg, #1e1e30 0%, #252540 100%)',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.02)',
                      boxShadow: '0 20px 40px rgba(255,107,107,0.25)',
                      border: '1px solid rgba(255,107,107,0.4)',
                    }
                  }}
                >
                  {/* Image Section */}
                  <Box sx={{ 
                    position: 'relative',
                    height: 160,
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    {offer.imageUrl ? (
                      <img 
                        src={offer.imageUrl} 
                        alt={offer.name || offer.productName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <Box 
                      sx={{ 
                        display: offer.imageUrl ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0
                      }}
                    >
                      <RestaurantMenuIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.8)' }} />
                    </Box>
                    
                    {/* Price Badge */}
                    <Box sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: 'linear-gradient(135deg, #00c853 0%, #00e676 100%)',
                      color: '#fff',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '20px',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      boxShadow: '0 4px 15px rgba(0,200,83,0.4)'
                    }}>
                      {formatPrice(price)}
                    </Box>
                    
                    {/* Combo Badge */}
                    {offer.isCombo && (
                      <Chip 
                        label="COMBO" 
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                  
                  {/* Content Section */}
                  <Box sx={{ p: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: '#fff',
                        mb: 1,
                        fontSize: '1rem',
                        lineHeight: 1.3,
                        minHeight: '2.6rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {offer.name || offer.productName}
                    </Typography>
                    
                    {/* Included Items */}
                    {includedItems.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.5 }}>
                          Includes:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {includedItems.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={item}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.1)',
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '0.65rem',
                                height: 22,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          ))}
                          {offer.productList?.length > 4 && (
                            <Chip
                              label={`+${offer.productList.length - 4} more`}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,107,107,0.2)',
                                color: '#ff6b6b',
                                fontSize: '0.65rem',
                                height: 22,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Add to Cart Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddShoppingCartIcon />}
                      onClick={() => handleOfferClick(offer)}
                      sx={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
                        color: '#fff',
                        fontWeight: 'bold',
                        py: 1,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        boxShadow: '0 4px 15px rgba(255,107,107,0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #ff8e53 0%, #ff6b6b 100%)',
                          boxShadow: '0 6px 20px rgba(255,107,107,0.5)',
                        }
                      }}
                    >
                      Add to Cart
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '300px',
            color: 'rgba(255,255,255,0.5)'
          }}>
            <LocalOfferIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6">No special offers available</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Check back later for exciting deals!
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SelfSpecialOffersDialog;
